import { useCallback } from 'react';
import { useCache, VARIABLE_SAVE_COST_INCLUDES } from 'src/graphql/useCache';
import { useUpsertAssessmentVariablesMutation, useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { MissingVariableValue, splitMissingVariableValues } from './util';

/**
 * Persists values entered in the MissingVariablesModal via `splitMissingVariableValues`
 * (see its doc for the scope rules), then refreshes every surface a variable value
 * feeds (variables lists + control costs) with ONE refetch include array — never
 * chain refetchQuery calls.
 */
export const useSaveMissingVariables = ({ assessmentId }: { assessmentId?: string }) => {
  const { profile } = useAuth();
  const { refetchQuery } = useCache();
  const [upsertWorkspaceVariables] = useUpsertWorkspaceVariablesMutation();
  const [upsertAssessmentVariables] = useUpsertAssessmentVariablesMutation();

  return useCallback(
    async (values: MissingVariableValue[]) => {
      const workspaceId = profile?.activeWorkspace?.id;
      const { workspaceVariables, assessmentVariables } = splitMissingVariableValues(values, !!assessmentId);

      if (workspaceVariables.length && workspaceId) {
        await upsertWorkspaceVariables({ variables: { workspaceId, variables: workspaceVariables } });
      }
      if (assessmentVariables.length && assessmentId) {
        await upsertAssessmentVariables({ variables: { assessmentId, variables: assessmentVariables } });
      }

      await refetchQuery({ include: VARIABLE_SAVE_COST_INCLUDES });
    },
    [profile?.activeWorkspace?.id, assessmentId, upsertWorkspaceVariables, upsertAssessmentVariables, refetchQuery],
  );
};
